/**
 * Wide, short guide screenshots -- a toolbar, a table header, one listing row
 * -- are unreadable squeezed to the column: a 1280x70 strip lands 42px tall.
 * Those are shown at their own size instead, clipped to the column, and panned
 * slowly from end to end, resting at each end. The reader can scroll it by
 * hand; hovering or touching it stops the pan for a while. Nothing moves for a
 * reader who asked for reduced motion; they get the scrollbar.
 *
 * Ported from csfme's src/components/guides/shot-pan.ts. The markup is
 * GuideShot's: a `[data-guide-shot]` frame holding a light and a dark
 * <picture>, one hidden by the theme class.
 */

/** Below this rendered height a strip is too small to read, so it pans at its own size. */
export const PAN_BELOW_PX = 140;
/** Pan speed, px per second. */
export const PAN_SPEED = 60;
/** How long the pan rests at each end, ms. */
export const PAN_REST_MS = 1500;
/** How long a reader's own scroll or touch holds the pan off, ms. */
export const PAN_HOLD_MS = 4000;
/** The class that switches a frame to natural size + horizontal scroll. */
export const PANNING_CLASS = 'guide-shot--pan';

/**
 * The capture harness shoots at deviceScaleFactor 2, then caps files at
 * CAPTURE_MAX_WIDTH (scripts/guide-capture/optimize.ts). So a file narrower
 * than the cap is an element at 2x -- a 186px status panel is a 372px file --
 * and one at the cap is about 1x, or a page shrunk to fit.
 */
export const CAPTURE_MAX_WIDTH = 1280;

/**
 * The width a capture was on screen in the app, in CSS px. A 2x element shows
 * at its real size, crisp on a retina screen, instead of blown up to the
 * column; a capped file keeps its width and max-width fits it to the column.
 */
export function cssWidth(naturalWidth: number): number {
  if (naturalWidth <= 0) return 0;
  return naturalWidth < CAPTURE_MAX_WIDTH ? Math.round(naturalWidth / 2) : naturalWidth;
}

/** Would this image, fitted to the frame's width, render too short to read? */
export function shouldPan(frameWidth: number, naturalWidth: number, naturalHeight: number): boolean {
  if (!frameWidth || !naturalWidth || !naturalHeight || naturalWidth <= frameWidth) return false;
  return (frameWidth * naturalHeight) / naturalWidth < PAN_BELOW_PX;
}

export interface PanState {
  /** 1 toward the right end, -1 back toward the left. */
  dir: 1 | -1;
  /** Time left resting at an end, ms. */
  rest: number;
}

/** One animation frame: the new scroll position and state after `dt` ms. */
export function panStep(pos: number, max: number, state: PanState, dt: number): { pos: number; state: PanState } {
  if (max <= 0) return { pos: 0, state };
  if (state.rest > 0) return { pos, state: { ...state, rest: Math.max(0, state.rest - dt) } };
  const next = pos + state.dir * PAN_SPEED * (dt / 1000);
  if (next >= max) return { pos: max, state: { dir: -1, rest: PAN_REST_MS } };
  if (next <= 0) return { pos: 0, state: { dir: 1, rest: PAN_REST_MS } };
  return { pos: next, state };
}

/**
 * The image the reader can see in this frame. The other theme's <picture> is
 * display:none, which is what gives it a null offsetParent.
 */
function visibleImage(frame: HTMLElement): HTMLImageElement | null {
  for (const img of Array.from(frame.querySelectorAll('img'))) {
    if (img.offsetParent !== null) return img;
  }
  return null;
}

/**
 * Give every loaded capture in the frame its on-screen width (cssWidth) as
 * its width attribute. The frame shrinks to fit it (guides.scss), and
 * max-width still caps it at the column.
 */
export function sizeImages(frame: HTMLElement): void {
  for (const img of Array.from(frame.querySelectorAll('img'))) {
    const w = img.complete ? cssWidth(img.naturalWidth) : 0;
    if (w && img.width !== w) img.width = w;
  }
}

/**
 * The width the frame may grow to: its container's. The frame itself is
 * fit-content, so its own width is the image's, not the room it has.
 */
function roomFor(frame: HTMLElement): number {
  return frame.parentElement?.clientWidth || frame.clientWidth;
}

/** Switch the frame between fitted and panning, from the visible image's size. */
export function decidePan(frame: HTMLElement): boolean {
  sizeImages(frame);
  const img = visibleImage(frame);
  const w = img ? cssWidth(img.naturalWidth) : 0;
  const h = img && img.naturalWidth ? Math.round((img.naturalHeight * w) / img.naturalWidth) : 0;
  const pan = !!img && img.complete && shouldPan(roomFor(frame), w, h);
  frame.classList.toggle(PANNING_CLASS, pan);
  if (!pan) frame.scrollLeft = 0;
  return pan;
}

/**
 * Set up one frame: decide now, again when an image loads, when the frame
 * resizes and when the theme flips (the other theme's capture becomes the
 * visible one); then pan while it is on screen. Returns the teardown, for a
 * React effect's cleanup.
 */
export function wireShotPan(frame: HTMLElement): () => void {
  const cleanups: (() => void)[] = [];
  const decide = () => decidePan(frame);

  for (const img of Array.from(frame.querySelectorAll('img'))) {
    img.addEventListener('load', decide);
    cleanups.push(() => img.removeEventListener('load', decide));
  }

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(decide);
    ro.observe(frame);
    cleanups.push(() => ro.disconnect());
  }

  // ThemeProvider flips classes on <html>; the pre-paint script and the layout
  // div follow the same value, so <html> is the one to watch.
  if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
    const mo = new MutationObserver(decide);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    cleanups.push(() => mo.disconnect());
  }

  decide();

  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return () => cleanups.forEach((fn) => fn());

  let heldUntil = 0;
  let hovered = false;
  const hold = () => {
    heldUntil = performance.now() + PAN_HOLD_MS;
  };
  const enter = () => {
    hovered = true;
  };
  const leave = () => {
    hovered = false;
  };
  const listeners: [string, EventListener][] = [
    ['wheel', hold],
    ['pointerdown', hold],
    ['touchstart', hold],
    ['pointerenter', enter],
    ['pointerleave', leave],
  ];
  for (const [event, fn] of listeners) {
    frame.addEventListener(event, fn, { passive: true });
    cleanups.push(() => frame.removeEventListener(event, fn));
  }

  // Tracked here, not read back from scrollLeft: browsers round scrollLeft to
  // whole pixels, and a 1px-per-frame pan would never leave the start.
  let pos = 0;
  let state: PanState = { dir: 1, rest: PAN_REST_MS };
  let last = 0;
  let onScreen = false;
  let raf = 0;
  let stopped = false;

  function tick(now: number) {
    raf = 0;
    if (stopped || !onScreen || !frame.isConnected) {
      last = 0;
      return;
    }
    const dt = last ? Math.min(now - last, 100) : 0;
    last = now;
    const max = frame.scrollWidth - frame.clientWidth;
    if (frame.classList.contains(PANNING_CLASS) && !hovered && now >= heldUntil) {
      if (Math.abs(frame.scrollLeft - pos) > 2) pos = frame.scrollLeft; // the reader scrolled it
      ({ pos, state } = panStep(pos, max, state, dt));
      frame.scrollLeft = pos;
    } else {
      pos = frame.scrollLeft;
    }
    raf = requestAnimationFrame(tick);
  }

  if (typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen && !raf) raf = requestAnimationFrame(tick);
    });
    io.observe(frame);
    cleanups.push(() => io.disconnect());
  }

  return () => {
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
    cleanups.forEach((fn) => fn());
  };
}
